import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { formatCompact } from '../components/MoneyText.js';
import { Card, ErrorNote, Row, Skeleton } from './Home.js';
import { UploadSheet } from '../components/UploadSheet.js';
import { PrimaryButton } from '../components/Sheet.js';

/**
 * EPF (docs/SCREEN-SPECS.md §2).
 *
 * One card per EPFO account, because a working life produces one per employer —
 * changing jobs opens a new member id under the same UAN. Showing them
 * separately is not a presentation choice: the accounts have different
 * balances, different employers, and one of them may have been emptied into
 * another, which is exactly the thing a single merged number would hide.
 */
interface Money {
  amount: number;
  currency: 'INR';
}

interface EpfAccount {
  id: string;
  member: { id: string; name: string };
  epfoMemberId: string;
  employer: string | null;
  transferredOut: boolean;
  balance: {
    employee: Money;
    employer: Money;
    pension: Money;
    total: Money;
    asOf: string | null;
  };
  latestFy: string | null;
  /** What the earliest passbook started from — not a transaction. */
  opening: { amount: number; currency: 'INR'; asOf: string | null } | null;
  lastRefreshedAt: string | null;
  entryCount: number;
}

interface EpfResponse {
  accounts: EpfAccount[];
  total: Money;
  pension: Money;
  lastRefreshedAt: string | null;
}

type EpfEntryKind = 'contribution' | 'interest' | 'transfer' | 'withdrawal' | 'other';

interface EpfEntry {
  month: string | null;
  date: string | null;
  particulars: string | null;
  kind: EpfEntryKind;
  wages: Money;
  employee: Money;
  employer: Money;
  pension: Money;
}

/**
 * How an entry is labelled. Interest is growth, a transfer is the same money
 * arriving from a previous employer, and a claim is money leaving — showing all
 * three as plain contributions would misdescribe the two largest entries in
 * this ledger.
 */
const KIND_LABEL: Record<EpfEntryKind, string> = {
  contribution: '',
  interest: 'Interest',
  transfer: 'Transfer',
  withdrawal: 'Withdrawal',
  other: '',
};

export function Epf() {
  const [uploading, setUploading] = useState(false);
  const [openAccount, setOpenAccount] = useState<string | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: ['epf'],
    queryFn: () => api.get<EpfResponse>('/epf'),
  });

  if (isPending) return <Skeleton />;
  if (error) return <ErrorNote message={(error as Error).message} />;

  return (
    <div>
      <h1 className="page-title">EPF</h1>

      <div style={{ marginBottom: 'var(--space-6)' }}>
        <div style={{ font: 'var(--type-title-1)' }}>{formatCompact(data.total.amount)}</div>
        <div className="page-sub">
          {/*
            Pension is stated beside the corpus, never inside it. EPS money
            cannot be withdrawn as a lump sum, so adding it here would overstate
            what the family actually has.
          */}
          across {data.accounts.length} account{data.accounts.length === 1 ? '' : 's'}
          {data.pension.amount > 0 && ` · pension ${formatCompact(data.pension.amount)} held separately`}
        </div>
        <div style={{ font: 'var(--type-caption)', color: 'var(--color-label-tertiary)', marginTop: 2 }}>
          {data.lastRefreshedAt
            ? `Last read from a passbook on ${data.lastRefreshedAt.slice(0, 10)}`
            : 'No passbook uploaded yet'}
        </div>
      </div>

      {data.accounts.length === 0 ? (
        <Card title="No passbook yet">
          <p
            style={{
              font: 'var(--type-footnote)',
              color: 'var(--color-label-secondary)',
              margin: 'var(--space-3)',
            }}
          >
            Download the passbook PDF for each financial year from the EPFO member portal and
            upload it here. One file per year, per employer — the transactions it contains will be
            read and added, and uploading the same year again changes nothing.
          </p>
        </Card>
      ) : (
        data.accounts.map((account) => (
          <AccountCard
            key={account.id}
            account={account}
            open={openAccount === account.id}
            onToggle={() => setOpenAccount(openAccount === account.id ? null : account.id)}
          />
        ))
      )}

      <PrimaryButton onClick={() => setUploading(true)} style={{ marginTop: 'var(--space-4)' }}>
        Upload a passbook
      </PrimaryButton>
      {uploading && <UploadSheet onClose={() => setUploading(false)} />}
    </div>
  );
}

function AccountCard({
  account,
  open,
  onToggle,
}: {
  account: EpfAccount;
  open: boolean;
  onToggle: () => void;
}) {
  const entries = useQuery({
    queryKey: ['epf-entries', account.id],
    queryFn: () => api.get<EpfEntry[]>(`/epf/${account.id}/entries`),
    enabled: open,
  });

  return (
    <Card
      title={`${account.employer ?? account.epfoMemberId} — ${formatCompact(account.balance.total.amount)}`}
    >
      <Row
        left={
          <span>
            <span style={{ display: 'block', font: 'var(--type-footnote)' }}>
              {account.member.name} · {account.epfoMemberId}
            </span>
            <span style={{ font: 'var(--type-caption)', color: 'var(--color-label-tertiary)' }}>
              {account.balance.asOf ? `Balance as on ${account.balance.asOf}` : 'No balance stated'}
              {account.latestFy && ` · passbook ${account.latestFy}`}
            </span>
            {/*
              A previous employer's account whose corpus has moved on. The
              passbook says so plainly — zero EPF with a pension remainder — and
              saying it here stops the account reading as money gone missing.
            */}
            {account.transferredOut && (
              <span
                style={{
                  display: 'block',
                  font: 'var(--type-caption)',
                  color: 'var(--color-status-warning)',
                }}
              >
                Transferred to a later account — only the pension remains here
              </span>
            )}
          </span>
        }
        right={
          <span style={{ textAlign: 'right', display: 'block' }}>
            <span style={{ font: 'var(--type-caption)', color: 'var(--color-label-tertiary)' }}>
              You {formatCompact(account.balance.employee.amount)}
              <br />
              Employer {formatCompact(account.balance.employer.amount)}
              {account.balance.pension.amount > 0 && (
                <>
                  <br />
                  Pension {formatCompact(account.balance.pension.amount)}
                </>
              )}
            </span>
          </span>
        }
      />

      <button
        onClick={onToggle}
        aria-expanded={open}
        style={{
          minHeight: 44,
          width: '100%',
          textAlign: 'left',
          padding: '0 var(--space-3)',
          border: 'none',
          borderTop: '1px solid var(--color-separator)',
          background: 'transparent',
          color: 'var(--color-tint)',
          font: 'var(--type-footnote)',
          cursor: 'pointer',
        }}
      >
        {open ? 'Hide' : 'Show'} {account.entryCount} transaction
        {account.entryCount === 1 ? '' : 's'}
      </button>

      {open &&
        (entries.isPending ? (
          <Skeleton />
        ) : (
          entries.data?.map((entry, i) => (
            <Row
              key={`${entry.date}-${i}`}
              left={
                <span>
                  <span style={{ display: 'block', font: 'var(--type-footnote)' }}>
                    {KIND_LABEL[entry.kind] && (
                      <span
                        style={{
                          font: 'var(--type-caption)',
                          color:
                            entry.kind === 'withdrawal'
                              ? 'var(--color-value-negative)'
                              : 'var(--color-tint)',
                          marginRight: 6,
                        }}
                      >
                        {KIND_LABEL[entry.kind]}
                      </span>
                    )}
                    {entry.particulars ?? '—'}
                  </span>
                  <span style={{ font: 'var(--type-caption)', color: 'var(--color-label-tertiary)' }}>
                    {/* The wage month and the date it was credited are different
                        things: March's contribution is posted in April. */}
                    {entry.month?.slice(0, 7)}
                    {entry.date && ` · credited ${entry.date}`}
                    {entry.wages.amount > 0 && ` · on wages ${formatCompact(entry.wages.amount)}`}
                  </span>
                </span>
              }
              right={
                <span style={{ textAlign: 'right', display: 'block' }}>
                  <span
                    style={{
                      font: 'var(--type-footnote)',
                      color:
                        entry.employee.amount + entry.employer.amount < 0
                          ? 'var(--color-value-negative)'
                          : 'inherit',
                    }}
                  >
                    {formatCompact(entry.employee.amount + entry.employer.amount)}
                  </span>
                  <span
                    style={{
                      display: 'block',
                      font: 'var(--type-caption)',
                      color: 'var(--color-label-tertiary)',
                    }}
                  >
                    you {formatCompact(entry.employee.amount)} · emp{' '}
                    {formatCompact(entry.employer.amount)}
                  </span>
                </span>
              }
            />
          ))
        ))}

      {/*
        The balance the earliest passbook OPENED with. Not a transaction — it is
        everything that accumulated before the years on file — so without it the
        list above does not add up to the balance shown, and the reader cannot
        tell history-we-never-had from a bug.
      */}
      {open && (account.opening?.amount ?? 0) !== 0 && (
        <Row
          left={
            <span>
              <span style={{ display: 'block', font: 'var(--type-footnote)' }}>
                Balance before the earliest passbook
              </span>
              <span style={{ font: 'var(--type-caption)', color: 'var(--color-label-tertiary)' }}>
                as on {account.opening?.asOf} · upload earlier years to see those transactions
              </span>
            </span>
          }
          right={
            <span style={{ font: 'var(--type-footnote)' }}>
              {formatCompact(account.opening!.amount)}
            </span>
          }
        />
      )}
    </Card>
  );
}
